import Image from '@/components/Image';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from 'next-intl';
import { useCallback, useContext, useMemo } from 'react';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import { Product } from './ProductCard';

const ProductTag = ({
  product,
  hideBestSellerTag,
}: {
  product: Product & { activity_style?: { product_label_image_pc: string } };
  hideBestSellerTag?: boolean;
}) => {
  const t = useTranslations('components.productTag');

  const seckillTag = useMemo(() => {
    try {
      return product?.activity_style?.product_label_image_pc || '';
    } catch (error) {
      logger.error(error);
      return '';
    }
  }, [product]);

  const { deviceType } = useContext(ResponsiveContext);

  const showBestSellerTag = useMemo(() => {
    try {
      if (typeof hideBestSellerTag === 'boolean') return !hideBestSellerTag;
      const isNotSpecialPromotion = !['3', '2'].includes(product.products_promotions_type);
      const isHot = product.is_hot === 1;
      return Boolean(isNotSpecialPromotion && isHot);
    } catch (error) {
      logger.error(error);
      return false;
    }
  }, [product]);

  const BestSellerTag = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ className = '' }: { className?: string }) => {
      if (!showBestSellerTag) {
        return null;
      }

      const baseClasses = 'bg-auxiliary-lilac-purple flex text-primary-black notranslate';
      const sizeClasses =
        deviceType === EPlatform.WEB
          ? 'items-center h-[24px] rounded-[24px] pr-10 pl-10 text-12'
          : 'items-center h-[18px] rounded-[18px] pr-6 pl-6 text-11';

      return (
        <span className={`${baseClasses} ${sizeClasses} ${className}`.trim()}>
          {t('bestSeller')}
        </span>
      );
    },
    [showBestSellerTag, deviceType, t],
  );

  if (product.products_promotions_type === '3') {
    if (seckillTag) {
      return (
        <>
          <Image
            className={`p-0 w-auto ${deviceType === EPlatform.WEB ? 'h-[24px]' : 'h-[18px]'}`}
            src={seckillTag}
            alt={t('seckill')}
            width={48}
            height={24}
            unoptimized
          />
          <BestSellerTag className="ml-[10px]" />
        </>
      );
    }
    return (
      <>
        <span
          className={`bg-auxiliary-rose-red flex items-center text-primary-black notranslate ${deviceType === EPlatform.WEB ? 'items-center h-[24px] rounded-[24px] pr-10 pl-10 text-12' : 'items-center h-[18px] rounded-[18px] pr-6 pl-6 text-11'}`}
        >
          {t('seckill')}
        </span>
        <BestSellerTag className="ml-[10px]" />
      </>
    );
  }
  if (product.products_promotions_type === '2') {
    return (
      <>
        <span
          className={`bg-auxiliary-rose-red flex items-center text-primary-black notranslate ${deviceType === EPlatform.WEB ? 'items-center h-[24px] rounded-[24px] pr-10 pl-10 text-12' : 'items-center h-[18px] rounded-[18px] pr-6 pl-6 text-11'}`}
        >
          {t('flashSale')}
        </span>
        <BestSellerTag className="ml-[10px]" />
      </>
    );
  }
  if (product.is_new_in === 1) {
    return (
      <>
        <span
          className={`bg-primary-green flex items-center text-primary-black notranslate ${deviceType === EPlatform.WEB ? 'items-center h-[24px] rounded-[24px] pr-10 pl-10 text-12' : 'items-center h-[18px] rounded-[18px] pr-6 pl-6 text-11 pt-1'}`}
        >
          {t('newIn')}
        </span>
        <BestSellerTag className="ml-[10px]" />
      </>
    );
  }
  return <BestSellerTag />;
};

export default ProductTag;
